/**
 * O identificador de um contato ou de uma conexão, do jeito que uma pessoa lê.
 *
 * Mora no `@hugi/ui` porque **três** builds precisam da mesma máscara — inbox, painel e console — e
 * até 2026-09-03 ela vivia só no inbox. O painel mostrava a coluna crua: um cartão dizia
 * `+55 27 99622-8174` (o `numero_exibicao` que a Meta manda já formatado) e o vizinho dizia
 * `5511910245347` (o `endereco` cru da API Hugi). O owner viu os dois lado a lado e perguntou por
 * que a máscara não era padrão. Não era porque não era UMA máscara.
 *
 * O que ela faz: número brasileiro de 12 ou 13 dígitos (com ou sem `+`) vira `+55 27 99622-8174`.
 * **Tudo o mais passa intacto** — um `@bot` do Telegram, um e-mail, um apelido como "Recepção", ou
 * um número que já veio formatado. Idempotente, de propósito: pode ser aplicada em cima do que a
 * Meta já formatou sem estragar nada.
 */
export function identificadorLegivel(bruto: string): string {
  const so = bruto.trim();
  if (!/^\+?\d{12,13}$/.test(so)) return bruto;
  const d = so.replace(/^\+/, "");
  const pais = d.slice(0, 2);
  const ddd = d.slice(2, 4);
  const resto = d.slice(4);
  // 9 dígitos = celular (5+4); 8 = fixo (4+4).
  const corpo = resto.length === 9 ? `${resto.slice(0, 5)}-${resto.slice(5)}` : `${resto.slice(0, 4)}-${resto.slice(4)}`;
  return `+${pais} ${ddd} ${corpo}`;
}

/**
 * O que a tela mostra como NOME de uma conexão: o apelido, se houver; senão o número legível.
 *
 * Separado do formatador porque a regra "apelido vence número" é de apresentação e vale igual nos
 * três builds — e porque um `??` distraído em cada tela é como uma delas fica mostrando o número
 * mesmo com apelido gravado.
 */
export function nomeDaConexao(c: {
  readonly apelido?: string | null;
  readonly numeroExibicao?: string | null;
  readonly endereco?: string | null;
}): string {
  const apelido = c.apelido?.trim();
  if (apelido) return apelido;
  const bruto = c.numeroExibicao ?? c.endereco;
  return bruto ? identificadorLegivel(bruto) : "(sem número)";
}
